/**
 * How this build of the panel runs: which data source, which router, whether the service
 * worker registers. Chosen once at start (src/main.tsx) from the Vite mode and the address.
 *
 * - production build: the real API (HTTP + SSE), history router, service worker. The mock
 *   is never reachable from a URL option: fake approvals must not show on the real panel.
 * - development (vite dev): the in-memory mock by default; `?api=live` uses the real API
 *   through the dev server's /api proxy.
 * - static preview (vite build --mode preview): the mock, a hash router and relative asset
 *   paths, so the build works when served from a sub-path with no index.html fallback.
 */

import type { MockMode } from '../api/mock/server.js';
import type { RouterMode } from './router.js';

export type DataSource = 'mock' | 'http';

export interface Runtime {
  dataSource: DataSource;
  router: RouterMode;
  mockMode: MockMode;
  serviceWorker: boolean;
}

/** The Vite mode name of the static preview build. */
export const PREVIEW_MODE = 'preview';

export function chooseRuntime(input: { mode: string; dev: boolean; search: string }): Runtime {
  const params = new URLSearchParams(input.search);
  const mockParam = params.get('mock');
  const mockMode: MockMode = mockParam === 'still' || mockParam === 'offline' ? mockParam : 'live';
  if (input.mode === PREVIEW_MODE) return { dataSource: 'mock', router: 'hash', mockMode, serviceWorker: false };
  if (input.dev) {
    const dataSource: DataSource = params.get('api') === 'live' ? 'http' : 'mock';
    return { dataSource, router: 'history', mockMode, serviceWorker: false };
  }
  return { dataSource: 'http', router: 'history', mockMode: 'live', serviceWorker: true };
}
